import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faUser,
  faBars,
  faSignOutAlt,
  faLocationDot,
  faFileInvoice,
  faUserPlus,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../components/Footer';
import { APP_ROUTES } from '../config/api.config';
import { getSubscriptions, getSubscribedLocations } from '../services/api';
import { useLogoutHandler } from '../utils/logoutHandler';
import LogoutConfirmDialog from '../components/LogoutConfirmDialog';
import Sidebar from '../components/Sidebar/Sidebar';
import logoMain from '../assets/images/logo-main.png';

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f5f6fa;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MobileHeader = styled.div`
  display: none;
`;

const HamburgerButton = styled.button`
  background: none;
  border: none;
  color: #333;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.3s ease;
  border: 1px solid #e0e0e0;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
`;

const MobileLogo = styled.div`
  @media (max-width: 768px) {
    img {
      height: 32px;
      width: auto;
    }
  }
`;

const Overlay = styled.div<{ isVisible: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: ${props => props.isVisible ? 1 : 0};
    visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
    transition: all 0.3s ease;
  }
`;

const Logo = styled.div<{ isCollapsed: boolean }>`
  padding: ${props => props.isCollapsed ? '0 10px' : '0 24px'};
  margin-bottom: 30px;
  text-align: ${props => props.isCollapsed ? 'center' : 'left'};

  img {
    max-width: ${props => props.isCollapsed ? '40px' : '160px'};
    height: auto;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MenuList = styled.ul<{ isCollapsed: boolean }>`
  list-style: none;
  padding: 0;
  margin: 0;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    flex-wrap: nowrap;
    background: #ff6b6b;
    padding: 12px 5px;
  }
`;

const MenuItem = styled.li<{ active?: boolean }>`
  margin: 8px 12px;
  border-radius: 8px;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 768px) {
    margin: 4px;
  }
`;

const MenuLink = styled(Link) <{ isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  color: white;
  text-decoration: none;
  font-size: 16px;

  svg {
    width: 20px;
    margin-right: ${props => props.isCollapsed ? '0' : '12px'};
    font-size: 18px;
  }

  span {
    display: ${props => props.isCollapsed ? 'none' : 'block'};
    font-size: 16px;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    
    span {
      display: none;
    }

    svg {
      margin-right: 0;
      font-size: 20px;
    }
  }
`;

const LogoutMenuItem = styled.button<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: white;
  font-size: 15px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 20px;
    margin-right: ${props => props.$isCollapsed ? '0' : '12px'};
  }

  span {
    display: ${props => props.$isCollapsed ? 'none' : 'block'};
  }
`;

const MainContent = styled.div<{ isCollapsed: boolean }>`
  flex: 1;
  margin-left: ${props => props.isCollapsed ? '70px' : '240px'};
  padding: 0;
  padding-bottom: 60px;
  padding-top: 76px;
  transition: margin-left 0.3s ease;
  width: calc(100% - ${props => props.isCollapsed ? '70px' : '240px'});

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    padding-top: 76px;
  }
`;

const TopBar = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
  position: fixed;
  top: 0;
  right: 0;
  left: ${props => props.isCollapsed ? '70px' : '240px'};
  z-index: 100;
  transition: left 0.3s ease;

  @media (max-width: 768px) {
    left: 0;
    width: 100%;
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #333;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.3s ease;
  border: 1px solid #e0e0e0;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
  
  @media (max-width: 768px) {
    justify-content: flex-end;
    padding: 0;
    gap: 8px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const UserName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #333;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #ff6b6b;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  margin-left: 8px;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const UserDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  display: ${props => props.isOpen ? 'block' : 'none'};
  z-index: 1000;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f8f9fa;
  }

  svg {
    width: 16px;
    margin-right: 12px;
  }
`;

// Remove or comment out the DropdownDivider styled component if it's not being used
// const DropdownDivider = styled.hr`
//   margin: 8px 0;
//   border: none;
//   border-top: 1px solid #e5e7eb;
// `;

const DashboardCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const CardTitle = styled.h2`
  font-size: 18px;
  color: #333;
  margin: 0 0 15px 0;
`;

const CardContent = styled.p`
  color: #666;
  font-size: 14px;
  margin: 0;
`;

const DashboardGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin: 20px;
  max-width: 100%;
  justify-content: flex-start;
`;

const ActivePlanCard = styled.div`
  background: #fff;
  border: 2px solid #ff8e53;
  border-radius: 20px;
  padding: 2rem 2.5rem 2.5rem 2.5rem;
  width: 350px;
  box-shadow: 0 4px 24px rgba(255,107,107,0.08);
  position: relative;
  text-align: left;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(255,107,107,0.12);
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 350px;
    padding: 1.5rem 2rem 2rem 2rem;
  }
`;

const MostPopularBadge = styled.div`
  position: absolute;
  top: -18px;
  right: 24px;
  background: #ff6b6b;
  color: #fff;
  border-radius: 20px;
  padding: 4px 24px;
  font-weight: 700;
  font-size: 16px;
  z-index: 2;
`;

const PlanTitle = styled.div`
  font-weight: 700;
  font-size: 22px;
  color: #222;
  margin-bottom: 8px;
`;

const PlanPrice = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #ff6b6b;
  margin-bottom: 0;
`;

const PlanPeriod = styled.span`
  font-size: 18px;
  color: #888;
  font-weight: 400;
`;

const PlanSub = styled.div`
  color: #888;
  font-size: 15px;
  margin-bottom: 16px;
`;

const NoPlanCard = styled(ActivePlanCard)`
  border: 2px dashed #ccc;
  color: #888;
  text-align: center;
  grid-column: 1 / -1;
  max-width: 600px;
  margin: 0 auto;
`;

const Dashboard: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activePlans, setActivePlans] = useState<any[]>([]);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get user data from localStorage that was saved during login
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userName = user.name || '';

  const {
    showLogoutConfirm,
    handleLogoutClick,
    handleLogoutConfirm,
    handleLogoutCancel
  } = useLogoutHandler();

  // Add click outside handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    // Attach the event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  useEffect(() => {
    const fetchActivePlans = async () => {
      setLoadingPlan(true);
      try {
        const result = await getSubscribedLocations();
        const subs = result.subscriptions || [];
        const active = subs.filter(
          (s) => ['active', 'authenticated', 'created'].includes(s.status)
        );

        // Add trial information to each plan
        const plansWithTrialInfo = active.map(plan => {
          const subscriptionCreatedDate = new Date(plan.subscriptionCreatedDate);
          const subscriptionEndDate = new Date(plan.subscriptionEndDate);

          // Calculate trial end date (14 days from creation)
          const trialEndDate = new Date(subscriptionCreatedDate);
          trialEndDate.setDate(trialEndDate.getDate() + 14);

          const now = new Date();
          const isTrialActive = now < trialEndDate && plan.status.toLowerCase() === 'authenticated';

          // Calculate due date based on trial status
          let dueDate: string;
          if (isTrialActive) {
            dueDate = trialEndDate.toLocaleDateString();
          } else {
            dueDate = subscriptionEndDate.toLocaleDateString();
          }

          return {
            ...plan,
            isTrialActive,
            trialEndDate: trialEndDate.toLocaleDateString(),
            actualDueDate: subscriptionEndDate.toLocaleDateString(),
            dueDate
          };
        });

        setActivePlans(plansWithTrialInfo);
      } catch {
        setActivePlans([]);
      } finally {
        setLoadingPlan(false);
      }
    };
    fetchActivePlans();
  }, []);

  const handleLogout = () => {
    handleLogoutClick();
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  return (
    <DashboardContainer>
      <Sidebar
        isCollapsed={isCollapsed}
        isMobileOpen={isMobileOpen}
        onMobileClose={toggleMobileMenu}
      />

      <MainContent isCollapsed={isCollapsed}>
        <TopBar isCollapsed={isCollapsed}>
          <ToggleButton onClick={() => {
            if (window.innerWidth <= 768) {
              toggleMobileMenu();
            } else {
              setIsCollapsed(!isCollapsed);
            }
          }}>
            <FontAwesomeIcon icon={faBars} />
          </ToggleButton>
          <UserProfile ref={dropdownRef} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            <UserInfo>
              <UserName>{userName}</UserName>
            </UserInfo>
            <UserAvatar>{userName.charAt(0)}</UserAvatar>
            <UserDropdown isOpen={isDropdownOpen}>
              <DropdownItem onClick={() => navigate('/profile')}>
                <FontAwesomeIcon icon={faUser} />
                Profile
              </DropdownItem>
              <DropdownItem onClick={handleLogout}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                Logout
              </DropdownItem>
            </UserDropdown>
          </UserProfile>
        </TopBar>

        {/* Active Plans Display */}
        {loadingPlan ? null : activePlans.length > 0 ? (
          <DashboardGrid>
            {activePlans.map((plan, index) => (
              <ActivePlanCard key={plan._id || index}>
                <PlanTitle>{plan.planName || 'Active Plan'}</PlanTitle>
                <PlanPrice>
                  {plan.planPrice ? `$${(plan.planPrice / 100).toFixed(2)}` : '--'}
                </PlanPrice>
                <PlanSub>Per Sub-Account</PlanSub>

                {/* Trial Status Indicator */}
                {plan.isTrialActive && (
                  <div style={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    background: '#fff0f0',
                    border: '1px solid #ff6b6b',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#ff6b6b',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>⏰</span>
                    <span>Trial Active - Ends: {plan.trialEndDate}</span>
                  </div>
                )}



                {plan.subAccounts && plan.subAccounts.length > 0 && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#666'
                  }}>
                    <strong>Connected Locations:</strong> {plan.subAccounts.length}
                  </div>
                )}
              </ActivePlanCard>
            ))}
          </DashboardGrid>
        ) : (
          <NoPlanCard>No active plans</NoPlanCard>
        )}

        <Footer isCollapsed={isCollapsed} />
      </MainContent>

      <LogoutConfirmDialog
        isOpen={showLogoutConfirm}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </DashboardContainer>
  );
};

export default Dashboard; 